'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface FullscreenImageViewerProps {
  images: string[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
  title?: string;
  autoRotateInterval?: number; // in milliseconds
}

const FullscreenImageViewer: React.FC<FullscreenImageViewerProps> = ({
  images,
  isOpen,
  onClose,
  initialIndex = 0,
  title = '',
  autoRotateInterval = 3000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Ensure component is mounted (for SSR compatibility)
  useEffect(() => {
    setMounted(true);
  }, []);

  // Navigation callbacks defined early
  const goToNext = useCallback(() => {
    setIsAutoRotating(false);
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setIsAutoRotating(false);
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToIndex = useCallback((index: number) => {
    setIsAutoRotating(false);
    setCurrentIndex(index);
  }, []);

  // Reset index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsAutoRotating(true);
    }
  }, [isOpen, initialIndex]);

  // Auto-rotate functionality
  useEffect(() => {
    if (!isOpen || !isAutoRotating || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [isOpen, isAutoRotating, images.length, autoRotateInterval]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goToNext, goToPrevious]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const viewerContent = (
    <div
      className='fixed inset-0 z-[99999] bg-black/95 flex items-center justify-center'
      onClick={onClose}
    >
      {/* Top Bar - Title and Counter */}
      <div className='absolute top-4 left-4 right-4 z-[100001] flex items-start justify-between'>
        {/* Title */}
        {title && (
          <div className='bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg'>
            <h3 className='text-white text-sm md:text-lg font-medium'>
              {title}
            </h3>
          </div>
        )}

        {/* Counter and Close Button */}
        <div className='flex items-center gap-3'>
          {/* Image counter */}
          {images.length > 1 && (
            <div className='bg-black/60 backdrop-blur-sm px-3 py-2 rounded-lg'>
              <p className='text-white text-xs font-medium'>
                {currentIndex + 1} / {images.length}
              </p>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className='p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors duration-200'
            aria-label='Close viewer'
          >
            <X className='w-6 h-6 md:w-8 md:h-8 text-white' />
          </button>
        </div>
      </div>

      {/* Main Image Container */}
      <div
        className='relative w-full h-full flex items-center justify-center px-4 md:px-16'
        onClick={e => e.stopPropagation()}
      >
        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className='absolute left-2 md:left-4 z-[100001] p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors duration-200'
              aria-label='Previous image'
            >
              <ChevronLeft className='w-6 h-6 md:w-8 md:h-8 text-white' />
            </button>

            <button
              onClick={goToNext}
              className='absolute right-2 md:right-4 z-[100001] p-2 md:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors duration-200'
              aria-label='Next image'
            >
              <ChevronRight className='w-6 h-6 md:w-8 md:h-8 text-white' />
            </button>
          </>
        )}

        {/* Image */}
        <div className='relative w-full h-full max-w-7xl max-h-[85vh] flex items-center justify-center'>
          <div className='relative w-full h-full'>
            <Image
              src={images[currentIndex] || '/assets/images/No-Image.jpg'}
              alt={`${title} - Image ${currentIndex + 1}`}
              fill
              className='object-contain'
              priority
              sizes='100vw'
            />
          </div>
        </div>
      </div>

      {/* Indicators */}
      {images.length > 1 && (
        <div className='absolute bottom-8 left-1/2 transform -translate-x-1/2 z-[100001] flex gap-2'>
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-2 h-2 bg-white scale-125'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Use portal to render at document body level, escaping any stacking contexts
  return createPortal(viewerContent, document.body);
};

export default FullscreenImageViewer;
